import { type Graph } from '../models/graph';

export function maxBipartiteMatching(
  graph: Graph,
  leftIds: readonly string[],
  rightIds: readonly string[],
): Map<string, string> {
  if (leftIds.length === 0 || rightIds.length === 0) return new Map();

  const leftSet = new Set(leftIds);
  const rightSet = new Set(rightIds);
  const adj = new Map<string, string[]>();
  for (const leftId of leftIds) adj.set(leftId, []);

  for (const edge of graph.edges) {
    if (leftSet.has(edge.sourceId) && rightSet.has(edge.targetId)) {
      adj.get(edge.sourceId)!.push(edge.targetId);
    }
    if (leftSet.has(edge.targetId) && rightSet.has(edge.sourceId)) {
      adj.get(edge.targetId)!.push(edge.sourceId);
    }
  }

  const matchRight = new Map<string, string | null>();
  for (const rightId of rightIds) matchRight.set(rightId, null);
  const matchLeft = new Map<string, string | null>();
  for (const leftId of leftIds) matchLeft.set(leftId, null);

  function augment(leftNode: string, visited: Set<string>): boolean {
    for (const rightNode of adj.get(leftNode) ?? []) {
      if (visited.has(rightNode)) continue;
      visited.add(rightNode);

      const currentMatch = matchRight.get(rightNode);
      if (currentMatch === null || currentMatch === undefined || augment(currentMatch, visited)) {
        matchLeft.set(leftNode, rightNode);
        matchRight.set(rightNode, leftNode);
        return true;
      }
    }
    return false;
  }

  for (const leftId of leftIds) augment(leftId, new Set());

  const result = new Map<string, string>();
  for (const leftId of leftIds) {
    const rightId = matchLeft.get(leftId);
    if (rightId) result.set(leftId, rightId);
  }
  return result;
}

export function inferBipartition(graph: Graph): { left: string[]; right: string[] } {
  const left = new Set<string>();
  const right = new Set<string>();
  for (const e of graph.edges) {
    left.add(e.sourceId);
    right.add(e.targetId);
  }
  for (const id of [...left]) {
    if (right.has(id)) {
      left.delete(id);
      right.delete(id);
    }
  }
  return { left: [...left], right: [...right] };
}
